import React from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { DropdownMenu } from '@raptormaps/dropdown-menu';
import type { DropdownItem } from '@raptormaps/dropdown-menu';
import { NavArea, NavGroupTrigger } from './AppHeader.styles';
import { growthbookInstance } from '../../utils/growthbookUtils';

interface NavItem {
  label: string;
  path: string;
  flagCheck?: () => boolean;
}

interface NavGroup {
  label: string;
  routes: string[];
  items?: NavItem[];
  isHome?: boolean;
  flagCheck?: () => boolean;
}

const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Operations',
    routes: [
      '/',
      '/order-status',
      '/upload',
      '/upload/form',
      '/project-onboarding',
      '/project',
      '/manage-module-types',
      '/user-management',
      '/roles',
    ],
    items: [
      { label: 'Order Status', path: '/order-status' },
      { label: 'Upload / Request Data', path: '/upload' },
      { label: 'Project Onboarding', path: '/project-onboarding' },
      { label: 'Project', path: '/project' },
      { label: 'Manage Module Types', path: '/manage-module-types' },
      { label: 'User Management', path: '/user-management' },
      { label: 'Roles', path: '/roles' },
    ],
  },
  {
    label: 'Organizations',
    routes: [
      '/org-users',
      '/org-permissions',
      '/org-allowed-list',
      '/create-organization',
      '/default-org-groups',
      '/default-client-org',
      '/manage-organizations',
      '/org-details',
    ],
    items: [
      { label: 'Org Users', path: '/org-users' },
      { label: 'Org Details', path: '/org-details' },
      { label: 'Org Permissions', path: '/org-permissions' },
      { label: 'Create Organization', path: '/create-organization' },
      { label: 'Default Org Groups', path: '/default-org-groups' },
      { label: 'Default Client Org', path: '/default-client-org' },
      { label: 'Manage Organizations', path: '/manage-organizations' },
      {
        label: 'Org Allowed List',
        path: '/org-allowed-list',
        flagCheck: () =>
          growthbookInstance.isOff('deprecate-org-allowed-list-page'),
      },
    ],
  },
  {
    label: 'Farms',
    routes: ['/farm-permissions', '/farm-details', '/farm-org-users'],
    items: [
      {
        label: 'Farm Permissions',
        path: '/farm-permissions',
        flagCheck: () =>
          growthbookInstance.isOn('raptor-admin-mco-farm-permissions'),
      },
      { label: 'Farm Details', path: '/farm-details' },
      { label: 'Farm Org Users', path: '/farm-org-users' },
    ],
  },
  {
    label: 'Data',
    routes: ['/copy-job', '/move-data', '/tasks'],
    items: [
      { label: 'Copy Job', path: '/copy-job' },
      { label: 'Move Data', path: '/move-data' },
      { label: 'Tasks', path: '/tasks' },
    ],
  },
  {
    label: 'Home',
    routes: ['/', '/home'],
    isHome: true,
    flagCheck: () => growthbookInstance.isOff('deprecate-home-page'),
  },
];

const HeaderNavGroups: React.FC = () => {
  const location = useLocation();
  const history = useHistory();

  const activeGroupLabel = NAV_GROUPS.find(group =>
    group.routes.includes(location.pathname),
  )?.label;

  return (
    <NavArea>
      {NAV_GROUPS.map(group => {
        const isActive = activeGroupLabel === group.label;

        // Home group: plain Link, hidden when deprecate-home-page is ON
        if (group.isHome) {
          if (group.flagCheck && !group.flagCheck()) {
            return null;
          }
          return (
            <Link
              key={group.label}
              to="/home"
              style={{ textDecoration: 'none' }}
            >
              <NavGroupTrigger className={isActive ? 'active' : ''}>
                {group.label}
              </NavGroupTrigger>
            </Link>
          );
        }

        // Filter flag-gated items
        const visibleItems: NavItem[] = (group.items ?? []).filter(item =>
          item.flagCheck ? item.flagCheck() : true,
        );

        const dropdownItems: DropdownItem[] = visibleItems.map(item => ({
          variant: 'option' as const,
          value: item.label,
          onSelect: () => history.push(item.path),
        }));

        return (
          <DropdownMenu key={group.label} items={dropdownItems}>
            <NavGroupTrigger className={isActive ? 'active' : ''}>
              {group.label}
            </NavGroupTrigger>
          </DropdownMenu>
        );
      })}
    </NavArea>
  );
};

export default HeaderNavGroups;
